import React, { useState } from 'react';
import { invokeFunction } from '../lib/contentApi';
import { uploadImagesToGoogleDrive } from '../lib/googleDriveUpload';
import { normalizeStoredImages } from '../lib/imageUtils';
import ImageUploader from './ImageUploader';

function EntryForm({ onSubmit, onCancel, initialType = 'note', initialData = null }) {
  const initialMetadata =
    initialData?.metadata && typeof initialData.metadata === 'object' ? initialData.metadata : {};

  const [formData, setFormData] = useState({
    id: initialData?.id || '',
    type: initialData?.displayType || initialData?.type || initialType,
    title: initialData?.title || '',
    content:
      initialData?.content ||
      ((initialData?.type || initialType) === 'note' ||
      (initialData?.type || initialType) === 'image'
        ? initialData?.body || ''
        : ''),
    url:
      initialData?.url ||
      ((initialData?.type || initialType) === 'link' ||
      (initialData?.type || initialType) === 'video'
        ? initialData?.body || ''
        : ''),
    tags: initialData?.tags?.join(', ') || '',
    images: normalizeStoredImages(initialData?.images || initialMetadata.images),
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleChange = (event) => {
    const { name, value } = event.target;
    setSubmitError('');
    setFormData((currentData) => ({
      ...currentData,
      [name]: value,
    }));
  };

  const handleTypeSelect = (type) => {
    setSubmitError('');
    setFormData((currentData) => ({
      ...currentData,
      type,
      content: type === 'link' || type === 'youtube' ? '' : currentData.content,
      url: type === 'note' || type === 'image' ? '' : currentData.url,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (isSubmitting) return;

    setSubmitError('');
    setIsSubmitting(true);

    try {
      const normalizedType = formData.type === 'youtube' ? 'video' : formData.type;
      const uploadedImages = await uploadImagesToGoogleDrive(formData.images);
      const normalizedTags = formData.tags
        ? formData.tags
            .split(',')
            .map((tag) => tag.trim())
            .filter(Boolean)
        : [];
      const usesTextBody = normalizedType === 'note' || normalizedType === 'image';
      const metadata = {
        ...initialMetadata,
        images: uploadedImages,
      };

      const payload = {
        ...initialData,
        id: formData.id || initialData?.id,
        type: normalizedType,
        title: formData.title.trim(),
        content: usesTextBody ? formData.content.trim() : '',
        url: usesTextBody ? '' : formData.url.trim(),
        tags: normalizedTags,
        metadata,
        images: uploadedImages,
      };

      if (typeof onSubmit === 'function') {
        await onSubmit(payload);
      } else {
        await invokeFunction('create-content', {
          type: payload.type,
          title: payload.title,
          body: usesTextBody ? payload.content : payload.url,
          tags: payload.tags,
          metadata: payload.metadata,
        });
      }

      onCancel();
    } catch (error) {
      console.error(error);
      setSubmitError(error.message || 'Failed to save');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isTextBodyType = formData.type === 'note' || formData.type === 'image';

  return (
    <div className="modal-backdrop">
      <div className="modal entry-form-modal">
        <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <img
              src="https://wallpapercave.com/wp/wp8601570.jpg"
              alt="Sanctum crest"
              style={{ width: '60px', height: '60px', borderRadius: '50%' }}
            />
          </div>
        </div>

        <div className="modal-header">
          <h2 className="modal-title">{initialData ? 'Edit Item' : 'Add New Item'}</h2>
          <button type="button" className="modal-close" onClick={onCancel}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="type-selector">
            <div
              className={`type-option ${formData.type === 'note' ? 'active note' : ''}`}
              onClick={() => handleTypeSelect('note')}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="type-option-icon"
                width="16"
                height="16"
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
                <polyline points="10 9 9 9 8 9" />
              </svg>
              Note
            </div>

            <div
              className={`type-option ${formData.type === 'link' ? 'active link' : ''}`}
              onClick={() => handleTypeSelect('link')}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="type-option-icon"
                width="16"
                height="16"
              >
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
              </svg>
              Link
            </div>

            <div
              className={`type-option ${formData.type === 'image' ? 'active image' : ''}`}
              onClick={() => handleTypeSelect('image')}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="type-option-icon"
                width="16"
                height="16"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <path d="M21 15l-5-5L5 21" />
              </svg>
              Image
            </div>

            <div
              className={`type-option ${formData.type === 'youtube' ? 'active youtube' : ''}`}
              onClick={() => handleTypeSelect('youtube')}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="type-option-icon"
                width="16"
                height="16"
              >
                <path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z" />
                <polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02" />
              </svg>
              YouTube
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="title" className="form-label">
              Title
            </label>
            <input
              type="text"
              id="title"
              name="title"
              className="form-control"
              placeholder="Title"
              value={formData.title}
              onChange={handleChange}
              required
            />
          </div>

          {isTextBodyType ? (
            <div className="form-group">
              <label htmlFor="content" className="form-label">
                {formData.type === 'image' ? 'Description' : 'Note Content'}
              </label>
              <textarea
                id="content"
                name="content"
                className="form-control"
                placeholder={
                  formData.type === 'image'
                    ? 'Describe this image set...'
                    : 'Write your note here...'
                }
                value={formData.content}
                onChange={handleChange}
              />
            </div>
          ) : (
            <div className="form-group">
              <label htmlFor="url" className="form-label">
                {formData.type === 'youtube' ? 'YouTube URL' : 'Link URL'}
              </label>
              <input
                type="url"
                id="url"
                name="url"
                className="form-control"
                placeholder={
                  formData.type === 'youtube'
                    ? 'https://www.youtube.com/watch?v=...'
                    : 'https://...'
                }
                value={formData.url}
                onChange={handleChange}
                required
              />
            </div>
          )}

          <ImageUploader
            images={formData.images}
            onChange={(images) => {
              setSubmitError('');
              setFormData((currentData) => ({ ...currentData, images }));
            }}
          />

          {submitError && (
            <div className="form-status-message error" role="alert">
              {submitError}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="tags" className="form-label">
              Tags (Optional, comma-separated)
            </label>
            <input
              type="text"
              id="tags"
              name="tags"
              className="form-control"
              placeholder="work, idea, watch later"
              value={formData.tags}
              onChange={handleChange}
            />
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={onCancel}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="btn-icon"
                width="16"
                height="16"
              >
                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                <polyline points="17 21 17 13 7 13 7 21" />
                <polyline points="7 3 7 8 15 8" />
              </svg>
              {initialData ? 'Update Item' : 'Save Item'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EntryForm;
